import { defineStore } from "pinia";
import { CommonHttp, SingleIdRequest } from "@/models/interfaces/http/common.http.request";
import { reactive, shallowReactive } from "vue";
import { rootStore } from "../index";
import { CustomParameterHintsResponse, DefaultParameterHintsResponse, ItemMeasureUnitSetRequest, ParameterContainerFilledHintsRequest, StorageItemAddRequest, 
  StorageItemNameContainer, StorageItemNameSettingsContainer, StorageItemNameSettingsSetRequest, StorageItemPriceEditAsker, 
  StorageItemSearched, StorageItemsSearchRequest, StorageDetailsEditRequest, StorageDetailsTemplateCreateRequest } from "@/models/interfaces/storage/storage-models";
import { AnalyticsElementIdentity, AnalyticsElementKind } from "@/models/interfaces/entity-settings/charts/charts";
import { AnalyticsV2ElementType } from "@/models/etc/types";
import { CommonEntity, NumericResponse, SingleIdRequestRecord, SingleIdResponse, StorageItemCloneResponse } from "@/models/interfaces/common";
import { MultiEditEntityContainer } from "../interfaces/types.analytics";
import { BaseListItem } from "../../components/common/base-list/interfaces/ListTypes";
import { queryStore } from "../querytable";


/**
 * Методы АПИ позиций складов
 */

// API методы по версиям

// v 9.0.7.0
// createStorageItem → POST api/storage/items

// v 9.0.8.0
// cloneStorageItem → POST api/storage/items/Clone/{itemId}

// v 9.0.12.0
// getStorageItemInfo → GET api/storage/items/GetInfo/{itemId}

// v 9.0.13.0
// editStorageItemPriceSettings → PUT api/storage/items/EditPriceSettings

// v 12.0.2.0
// addFileToStorageItem → POST api/storage/items/AddFile

// v 12.0.3.0
// editStorageItemMeasureUnit → PUT api/storage/items/EditMeasureUnit
// getStorageItemMeasureUnits → GET api/storage/items/GetMeasureUnits

// v 12.0.4.0
// deleteFileFromStorageItem → DELETE api/storage/items/File/{fileId}
// deleteStorageItem → DELETE api/storage/items/{itemId}

// v 12.0.5.0
// addStorageItemNameElement → POST api/storage/items/AddNameElement
// deleteStorageItemNameElement → DELETE api/storage/items/NameElement/{elementId}

// v 12.1.5.0
// createStorageItem → POST api/storage/items (обновлённый URI)

// v 12.1.6.0
// getPositionNameSettings → GET api/storage/item-name-settings
// setPositionNameSettings → PUT api/storage/item-name-settings

// v 15.4.3.0
// createNoteTemplate → POST api/storage/item-details-templates
// deleteNoteTemplate → DELETE api/storage/item-details-templates/{templateId}
// getNoteTemplates → GET api/storage/item-details-templates

// v 12.2.15.0
// getItemsForNewMovement → POST api/storage/items/for-new-movement

// v 12.5.0.0
// searchStorageItems → POST api/storage/items/search

// v 12.5.1.0
// getStorageItemNameContainer → GET api/storage/items/name-container

// v 12.5.5.0
// getPriceByLastIncome → GET api/storage/items/{itemId}/price-by-last-income

// v 15.4.1.0
// editStorageItemDetails → PUT api/storage/items/EditDetails

const API_URL = process.env.VUE_APP_API_URL

export type QueryState = {
    loading: boolean;
    // ignore the request result and don't mutate the model after the request is finished
    stop_request: boolean;
    // настройки отображения названия позиций склада (для настроек)
    position_name_settings: StorageItemNameSettingsContainer;
    // детальная информация о позиции склада.
    // item_info: StorageItemInfo;
    // список единиц измерения для позиций склада.
    measure_units: DefaultParameterHintsResponse;
    storage_template_container: StorageItemNameContainer;
    // отфильтрованный список позиций склада для создания движения.
    // searched_items: StorageItemSearched[];
    // список шаблонов комментария позиции склада, отсортированный по частоте использования.
    // note_templates: CommonEntityRecordList;

    // ID новой (склонированной) позиции склада.
    cloned_item: StorageItemCloneResponse;
    // новый шаблон комментария позиции склада.
    note_template_created: CommonEntity;

    // asynchronous queue
    reqQueue: Map<string, AbortController>;
};

export const storagePositionsStore = defineStore("storagePositionsStore", {
  state: () =>
    ({
        loading: false,
        // ignore the request result and don't mutate the model after the request is finished
        stop_request: false,
        // reactive filters change and update directly from interface
        position_name_settings: shallowReactive({} as StorageItemNameSettingsContainer),
        // item_info: shallowReactive({} as StorageItemInfo),
        measure_units: shallowReactive({} as DefaultParameterHintsResponse),
        storage_template_container: shallowReactive({} as StorageItemNameContainer),
        // searched_items: shallowReactive({} as StorageItemSearched[]),
        price_by_last_income: shallowReactive({} as NumericResponse),
        // note_templates: shallowReactive({} as CommonEntityRecordList),

        created_item_id: shallowReactive({} as SingleIdResponse),
        cloned_item: shallowReactive({} as StorageItemCloneResponse),
        added_file_id: shallowReactive({} as SingleIdResponse),

        note_template_created: shallowReactive({} as CommonEntity),

        reqQueue: new Map()
    } as QueryState),

  actions: {

    // GET

    // GET position name settings
    async getPositionNameSettings() {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'global_position_name_settings';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response = undefined
        await http.get(`storage/item-name-settings`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.position_name_settings = reactive({} as StorageItemNameSettingsContainer);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.position_name_settings = reactive(data);

          this.loading = false
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // GET storage item info
    async getStorageItemInfo(itemId: number) {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storage_item_info';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/items/GetInfo/${itemId}`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // this.item_info = reactive({} as StorageItemInfo);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.item_info = reactive(data);

          this.loading = false
          return reactive(data);
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // GET storage item measure units
    async getStorageItemMeasureUnits() {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storage_measure_units';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/items/GetMeasureUnits`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.measure_units = reactive({} as DefaultParameterHintsResponse);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.measure_units = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // GET storage item name container
    async getStorageItemNameContainer (
      elementId?: number,
      kind?: AnalyticsElementKind,
      source?: AnalyticsV2ElementType,
      destination?: AnalyticsV2ElementType
    ): Promise<StorageItemNameContainer | undefined> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'name_container'+kind+'-'+(source === undefined ? 'null' : source)+ 
          '-'+destination+'-'+elementId;

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined

        let params: string = "";
        if (elementId) {
          params += `?elementId=${elementId}`;
          if (kind) params += `&kind=${kind}`
          if (source) params += `&source=${source}`
          if (destination) params += `&destination=${destination}`
        }

        await http.get(`storage/items/name-container${params}`, { 
          signal: controller.signal
        }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // this.storage_template_container = reactive({} as StorageItemNameContainer);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.storage_template_container = reactive(data);

          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false;
      return undefined;
    },

    // TODO: transfer to parameters store

    // GET hints to field (default)
    async parameterInstanceGetHintsToField(
      obj: SingleIdRequest,
      count: number,
      offset: number,
      filter: string
    ): Promise<DefaultParameterHintsResponse> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = `hints_field_default_${obj.Id}_${count}_${offset}`
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(
          `parameters/instance/hints/recordFieldInstance/default?HintedInstanceId=${obj.Id}&Count=${count}&Offset=${offset}&Filter=${filter}`,
          { signal: controller.signal }
        )
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          // this.hints_field_default = reactive(response.data as DefaultParameterHintsResponse)
          const { data } = response;
          this.loading = false
          return reactive(data as DefaultParameterHintsResponse)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as DefaultParameterHintsResponse
    },

    // GET hints to field (custom)
    async parameterInstanceGetHintsToFieldCustom(
      obj: SingleIdRequest,
      filter: string
    ): Promise<CustomParameterHintsResponse> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = `hints_field_custom_${obj.Id}`
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(
          `parameters/instance/hints/recordFieldInstance/custom?HintedInstanceId=${obj.Id}&Count=1&Offset=0&Filter=${filter}`,
          { signal: controller.signal }
        )
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          // this.hints_field_custom = reactive(response.data as CustomParameterHintsResponse)
          const { data } = response;
          this.loading = false
          return reactive(data as CustomParameterHintsResponse)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as CustomParameterHintsResponse
    },

    // GET hints to parameter
    async parameterInstanceGetHintsToParameter(
      obj: SingleIdRequest,
      count: number,
      offset: number,
      filter: string
    ): Promise<DefaultParameterHintsResponse> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = `hints_parameter_default_${obj.Id}_${count}_${offset}`
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(
          `parameters/instance/hints/parameterInstance/default?HintedInstanceId=${obj.Id}&Count=${count}&Offset=${offset}&Filter=${filter}`,
          { signal: controller.signal }
        )
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          // this.hints_parameter_default = reactive(response.data as DefaultParameterHintsResponse)
          const { data } = response;
          this.loading = false
          return reactive(data as DefaultParameterHintsResponse)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as DefaultParameterHintsResponse
    },

    // END TODO

    // POST hints to storage container (default)
    async instanceGetHintsToInstanceContainerFilledDefault(
      obj: ParameterContainerFilledHintsRequest
    ): Promise<DefaultParameterHintsResponse> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = `hints_storage_default_${obj.HintedTemplateId || 'global'}`
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(
          `parameters/instance/hints/storageContainer/default`,
          obj,
          { signal: controller.signal }
        )
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          // this.hints_storage_default = reactive(response.data as DefaultParameterHintsResponse)
          const { data } = response;
          // console.log(data)
          this.loading = false
          return reactive(data as DefaultParameterHintsResponse)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as DefaultParameterHintsResponse
    },

    // POST hints to storage container (custom)
    async instanceGetHintsToInstanceContainerFilledCustom(
      obj: ParameterContainerFilledHintsRequest
    ): Promise<CustomParameterHintsResponse> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = `hints_storage_custom_${obj.HintedTemplateId || 'global'}`
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(
          `parameters/instance/hints/storageContainer/custom`,
          obj,
          { signal: controller.signal }
        )
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          const { data } = response;
          this.loading = false
          return reactive(data as CustomParameterHintsResponse)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as CustomParameterHintsResponse
    },

    // POST search storage items
    async searchStorageItems(arg: StorageItemsSearchRequest): Promise<StorageItemSearched[] | undefined> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storage_items_search'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`storage/items/search`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response && (response.status === 200 || response.status === 201)) {
          const { data } = response;
          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e: any) {
        if (e?.name !== 'AbortError') console.log(JSON.stringify(e))
        this.reqQueue.delete(entkey)
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // GET price by last income
    async getPriceByLastIncome(itemId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/items/${itemId}/price-by-last-income`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return data
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // GET note templates
    async getNoteTemplates(value?: string, count?: number, offset?: number) {
      const rootstore = rootStore()
      this.stop_request = false

      // const entkey = 'note_templates';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      // Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
      //   if (key === entkey) {
      //     console.log('abort conn '+key)
      //     this.reqQueue.get(key)?.abort();
      //     this.reqQueue.delete(key);
      //   }
      // });

      // const controller = new AbortController();
      // this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined

        await http.get(`storage/item-details-templates?value=${value}&count=${count}&offset=${offset}`).then(res => {
          response = res
          // this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          // this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // this.note_templates = reactive({} as CommonEntityRecordList);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.note_templates = reactive(data);

          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // EDIT

    // POST get storage items for new movement
    async getItemsForNewMovement(arg: MultiEditEntityContainer) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/items/for-new-movement`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT position name settings
    async setPositionNameSettings(arg: StorageItemNameSettingsSetRequest) {
      const rootstore = rootStore()
      this.stop_request = false

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/item-name-settings`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST create storage item
    async createStorageItem(arg: StorageItemAddRequest) {
      const rootstore = rootStore()
      this.stop_request = false

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/items`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // PUT edit storage item price settings
    async editStorageItemPriceSettings(arg: StorageItemPriceEditAsker) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/items/EditPriceSettings`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST clone storage item
    async cloneStorageItem(itemId: number) {
      const rootstore = rootStore()
      this.stop_request = false

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/items/Clone/${itemId}`, {}).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.cloned_item = reactive({} as StorageItemCloneResponse);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.cloned_item = reactive(data);

          const querystore = queryStore();
          querystore.stripTableRows(itemId, this.cloned_item.Id, this.cloned_item.VendorCode, true);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT edit storage item details
    async editStorageItemDetails(arg: StorageDetailsEditRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/items/EditDetails`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          const { data } = response;
          this.loading = false
          return { response_ok: true, data }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false, data: undefined }
    },

    // POST add name element to storage item
    async addStorageItemNameElement(arg: SingleIdRequestRecord) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/items/AddNameElement`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE storage item name element
    async deleteStorageItemNameElement(elementId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/items/NameElement/${elementId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST add file to storage item comment
    async addFileToStorageItem(file: File, entityId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT, false, true)
      try {
        this.loading = true
        let response: any = undefined

        const formData = new FormData()
        formData.append('File', file)
        formData.append('EntityId', entityId.toString())

        await http.post(`storage/items/AddFile`, formData).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return data
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // DELETE file from storage item comment
    async deleteFileFromStorageItem(fileId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/items/File/${fileId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT edit storage item measure unit
    async editStorageItemMeasureUnit(arg: ItemMeasureUnitSetRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/items/EditMeasureUnit`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE storage item
    async deleteStorageItem(itemId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/items/${itemId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false

          const querystore = queryStore();
          querystore.stripTableRows(itemId, 0, '');

          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST create note template
    async createNoteTemplate(arg: StorageDetailsTemplateCreateRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/item-details-templates`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.note_template_created = reactive({} as CommonEntity);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.note_template_created = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE note template
    async deleteNoteTemplate(templateId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/item-details-templates/${templateId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    clearSettings() {
      // reactive filters change and update directly from interface
      this.clearCommon();
    },

    clearCommon() {
      // reactive filters change and update directly from interface
      this.position_name_settings = reactive({} as StorageItemNameSettingsContainer);
    },

    async reloadNameContainer(currentItem: AnalyticsElementIdentity) {
      this.storage_template_container = reactive({} as StorageItemNameContainer);
      let data = undefined;

      if (currentItem && currentItem.ElementId) {
        if (![7,8].includes(currentItem.Destination)) { return }
        data = await this.getStorageItemNameContainer(currentItem.ElementId, currentItem.Kind, currentItem.Source, currentItem.Destination)
      } else {
        data = await this.getStorageItemNameContainer()
      }

      if (data) {
        this.storage_template_container = reactive(data);
      }
    },

    async initData() {
      this.getStorageItemMeasureUnits();
      const data = await this.getStorageItemNameContainer();
      if (data) {
        this.storage_template_container = reactive(data);
      }
    },
  },

  getters: {
    
    isLoading(state): boolean {
      return state.loading
    },

    measureUnits(state): DefaultParameterHintsResponse | undefined {
      return state.measure_units && state.measure_units.HintedSetName ? state.measure_units : undefined
    },

    currentItemNameContainer(state): StorageItemNameContainer | undefined {
      return state.storage_template_container && state.storage_template_container.ParameterContainer ? state.storage_template_container : undefined
    }
  }
});